import type {
  ApplicantConsentInput,
  ApplicantLegal,
  ApplicantConsentState,
  ApplicationAttachment,
  ApplicationDraft,
  SaveStepResult,
  SubmitApplicationInput,
  SubmitApplicationResult,
} from "@unitfield/sdk";

/**
 * The apply flow's browser-side client for the storefront BFF (`/apply/api/*`, same origin). The browser never holds a token:
 * the BFF reads the sealed `__Host-uf_sf` cookie and calls `/api/customer/v1/applications` as the signed-in customer.
 */
export class ApplyRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "ApplyRequestError";
  }

  /** The customer's tokens are gone or revoked: the page restarts sign-in. */
  get sessionEnded(): boolean {
    return this.status === 401;
  }
}

async function call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  return send<T>(method, path, body === undefined ? undefined : JSON.stringify(body), { ...(body === undefined ? {} : { "content-type": "application/json" }), ...headers });
}

async function send<T>(method: string, path: string, body: BodyInit | undefined, headers: Record<string, string>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/apply/api${path}`, { method, headers, body, credentials: "same-origin", cache: "no-store" });
  } catch {
    throw new ApplyRequestError("The request could not be completed. Check your connection and try again.", 0, null);
  }
  const text = await response.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (!response.ok) {
    const failure = (parsed && typeof parsed === "object" ? parsed : {}) as { message?: unknown; code?: unknown };
    throw new ApplyRequestError(typeof failure.message === "string" && failure.message ? failure.message : "Request failed", response.status, typeof failure.code === "string" ? failure.code : null);
  }
  return parsed as T;
}

const id = (value: string) => encodeURIComponent(value);

export const applyApi = {
  legal: () => call<ApplicantLegal>("GET", "/legal"),
  createDraft: (unitPublicKey: string) => call<ApplicationDraft>("POST", "/drafts", { unitPublicKey }),
  recordConsent: (draftId: string, consent: ApplicantConsentInput) => call<ApplicantConsentState>("PUT", `/drafts/${id(draftId)}/consent`, consent),
  saveStep: (draftId: string, stepKey: string, payload: unknown) => call<SaveStepResult>("PUT", `/drafts/${id(draftId)}/steps/${id(stepKey)}`, { payload }),
  /**
   * One request: the file itself goes to the BFF, which stores it and completes the upload. The browser never calls storage
   * (no CORS, no storage host in the page's CSP), so this works the same on every host.
   */
  uploadAttachment: (draftId: string, attachmentType: string, file: File) =>
    send<ApplicationAttachment>("POST", `/drafts/${id(draftId)}/attachments?${new URLSearchParams({ type: attachmentType, name: file.name })}`, file, { "content-type": file.type || "application/octet-stream" }),
  /** The idempotency key makes a retry after a lost response return the same application instead of a second one. */
  submit: (draftId: string, input: SubmitApplicationInput, idempotencyKey: string) =>
    call<SubmitApplicationResult>("POST", `/drafts/${id(draftId)}/submit`, input, { "idempotency-key": idempotencyKey }),
};
